import React, {useState, useEffect, useRef, useMemo} from "react";
import {PerformerCard, HorizontalBarMetrics, CompetencyAssessment} from "../../layouts/ChartLayout"
import { FaFilter, FaTimes } from "react-icons/fa";
import ConversationRecords from "../calls/ConversationRecords";
import server from "../../../api/server";
import "../../../dashboard-components/loader.css"
import ModernTimeDropdown from "../../layouts/modernDropdown";
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker";
import CallAdvancedFiltersPanel from "../../layouts/CallAdvancedFiltersPanel";
import { Download, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { useNavigate, useLocation } from 'react-router-dom';
import {
  initializeFiltersFromUrl,
  updateUrlWithFilters,
  extractModuleParameterFiltersFromFlatFilters,
  getCrmLeadCreatedAtApiArgs,
  buildCallsPageDrilldownFilterApplied,
  getConversationSegmentAdvancedFilterPatch,
} from '../../../utils/filterUtils';
import CrmLeadCreatedAtFilterChips, { shouldHideCrmLeadCreatedAtChipKey } from "../../layouts/CrmLeadCreatedAtFilterChips";
import { downloadRowsAsWorkbook, downloadWorkbook } from "../../../utils/chartDownloadUtils";
import {
  buildAdditionalTagFilterConditions,
  getAdditionalTagFilterValues,
} from "../../../utils/filterPayloadBuilder";

const toSnakeCase = (value) => String(value || '').trim().replace(/([A-Z])/g, '_$1').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();
const toTitleCase = (value) => String(value || '').replace(/\b\S/g, (char) => char.toUpperCase());
const competencyHeaderLabels = {
  agent_talk_duration: 'Agent Talk Duration % (ex dead air)',
  customer_talk_duration: 'Customer Talk Duration % (ex dead air)',
  calls_where_customer_talk_duration_lt_20: 'Calls Where Customer Talk Duration LT 20%',
};
const competencyHeaderSuffixes = {
  aht: 'AHT (secs)',
  total_talk_time: 'Total Talk Time (mins)',
};
const formatCompetencyColumnLabel = (col) => {
  const normalizedCol = toSnakeCase(col);
  if (competencyHeaderLabels[normalizedCol]) return competencyHeaderLabels[normalizedCol];
  if (competencyHeaderSuffixes[normalizedCol]) return competencyHeaderSuffixes[normalizedCol];
  return toTitleCase(String(col || '').replaceAll('_', ' '));
};
const asValidBetween = (value) => {
  if (!value || typeof value !== 'object') return null;
  const lowerRaw = value.lower;
  const upperRaw = value.upper;
  const lower = lowerRaw === '' || lowerRaw === null || lowerRaw === undefined ? null : Number(lowerRaw);
  const upper = upperRaw === '' || upperRaw === null || upperRaw === undefined ? null : Number(upperRaw);
  const hasLower = lower !== null && !Number.isNaN(lower);
  const hasUpper = upper !== null && !Number.isNaN(upper);
  if (!hasLower && !hasUpper) return null;
  return { lower: hasLower ? lower : null, upper: hasUpper ? upper : null };
};
const buildModuleParameterFilters = (filters = {}, advancedFilters = {}) => {
  const output = [
    ...extractModuleParameterFiltersFromFlatFilters(filters),
    ...buildAdditionalTagFilterConditions(advancedFilters),
  ];
  if (Array.isArray(advancedFilters.agent_feedback_multiple)) {
    advancedFilters.agent_feedback_multiple.forEach((item) => {
      const field = toSnakeCase(item?.agent_feedback_parameter);
      const lower = item?.agent_feedback_parameter_score_lower;
      const upper = item?.agent_feedback_parameter_score_upper;
      if (field && (lower !== null && lower !== undefined && lower !== '' || upper !== null && upper !== undefined && upper !== '')) {
        output.push({ field, operator: 'between', value: { lower, upper }, module: 'agent_feedback' });
      }
    });
  }
  if (Array.isArray(advancedFilters.business_insights_multiple)) {
    advancedFilters.business_insights_multiple.forEach((item) => {
      const field = toSnakeCase(item?.business_insights_parameter);
      const value = item?.business_insights_parameter_value;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'business_insight' });
      } else if (field && value !== null && value !== undefined && value !== '') {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'business_insight' });
      }
    });
  }
  if (advancedFilters.custom_module_parameter_name && advancedFilters.custom_module_parameter_answer !== null && advancedFilters.custom_module_parameter_answer !== undefined && advancedFilters.custom_module_parameter_answer !== '') {
    const betweenValue = asValidBetween(advancedFilters.custom_module_parameter_answer);
    output.push({
      field: toSnakeCase(advancedFilters.custom_module_parameter_name),
      operator: betweenValue ? 'between' : Array.isArray(advancedFilters.custom_module_parameter_answer) ? 'in' : 'eq',
      value: betweenValue || advancedFilters.custom_module_parameter_answer,
      module: 'custom_module'
    });
  }
  if (Array.isArray(advancedFilters.custom_module_multiple)) {
    advancedFilters.custom_module_multiple.forEach((item) => {
      const field = toSnakeCase(item?.custom_module_parameter_name);
      const value = item?.custom_module_parameter_answer;
      const betweenValue = asValidBetween(value);
      if (field && betweenValue) {
        output.push({ field, operator: 'between', value: betweenValue, module: 'custom_module' });
      } else if (field && value !== null && value !== undefined && value !== '') {
        output.push({ field, operator: Array.isArray(value) ? 'in' : 'eq', value, module: 'custom_module' });
      }
    });
  }
  const dedupe = [];
  const seen = new Set();
  output.forEach((item) => {
    const key = JSON.stringify([item.field, item.operator, item.module, item.value]);
    if (seen.has(key)) return;
    seen.add(key);
    dedupe.push(item);
  });
  return dedupe;
};
const buildParameterQueryFilters = (conditions = []) => {
  const query = {};
  conditions.forEach((condition) => {
    if (!condition.module) return;
    const keyPrefix = condition.module ? `${condition.module}_${condition.field}` : condition.field;
    if (condition.operator === 'between' && condition.value && typeof condition.value === 'object') {
      const lowerKey = condition.module === 'agent_feedback' ? `${keyPrefix}_score_lower` : `${keyPrefix}_lower`;
      const upperKey = condition.module === 'agent_feedback' ? `${keyPrefix}_score_upper` : `${keyPrefix}_upper`;
      if (condition.value.lower !== undefined && condition.value.lower !== null && condition.value.lower !== '') {
        query[lowerKey] = condition.value.lower;
      }
      if (condition.value.upper !== undefined && condition.value.upper !== null && condition.value.upper !== '') {
        query[upperKey] = condition.value.upper;
      }
      return;
    }
    if (condition.value !== undefined && condition.value !== null && condition.value !== '') {
      query[keyPrefix] = condition.value;
    }
  });
  return query;
};

const AgentFeedbackAgentWise = ({serviceID, clientID}) => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Default filter values
    const defaultFilters = {
        employee: '',
        team_name: '',
        time: 'Last 7 Days',
        audioduration: '',
        audioDurationLower: '',
        audioDurationUpper: '',
        record_type: '',
        customRangeType: 'between',
        customStart: '',
        customEnd: ""
    };
    const defaultAdvancedFilters = {
      fatal: '',
      type: '',
      conversation_type_reason: '',
      conversationPotential: '',
      refusalCategory: '',
      conversationOutcome: '',
      openConcerns: '',
      customerId: '',
      audioDuration: '',
      scoreLower: null,
      scoreUpper: null,
      agent_feedback_multiple: [],
      business_insights_multiple: [],
      disposition: '',
      subdisposition: '',
      concernCategory: '',
      concernSubcategory: '',
      queryCategory: '',
      querySubcategory: '',
      conversation_direction: '',
      lead_source: '',
      primary_language: '',
      secondary_languages: '',
      lead_sub_source: '',
      tag: '',
      tag2: '',
      tag3: '',
      tag4: '',
      tag5: '',
      tag6: '',
      tag7: '',
      tag8: '',
      tag9: '',
      tag10: '',
      tag11: '',
      tag12: '',
      tag13: '',
      tag14: '',
      tag15: '',
      tag16: '',
      tag17: '',
      tag18: '',
      tag19: '',
      tag20: '',
      tag21: '',
      tag22: '',
      tag23: '',
      tag24: '',
      tag25: '',
      tag26: '',
      tag27: '',
      tag28: '',
      tag29: '',
      tag30: '',
      first_proper_conversation: '',
      campaignId: '',
      actorType: '',
      crm_lead_stage: '',
      crm_lead_sub_stage: '',
      customer_phone_number: '',
      crm_location: '',
      crm_lead_created_at: '',
      crm_lead_created_at_start: '',
      crm_lead_created_at_end: '',
      crm_lead_created_at_range_type: 'between',
      custom_module_multiple: [],
      custom_module_parameter_name: '',
      custom_module_parameter_answer: '',
    };

    // Initialize filters from URL or use defaults
    const initialFilterState = initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters);

    const [client_id, setClient_id] = useState(null)
    const [email, setEmail] = useState(null)
    const [isLoading, setIsLoading] = useState(false) // legacy; can be removed later
    // granular loading states
    const [loadingMetrics, setLoadingMetrics] = useState(true);
    const [loadingCompetency, setLoadingCompetency] = useState(true);
    // debounce refs
    const filtersDebounceRef = useRef(null);
    const firstFiltersLoadRef = useRef(true);
    const [performanceMetrics, setPerformaceMetrics] = useState(null)
    const [agentWiseDailyTrendData, setAgentWiseDailyTrendData] = useState([])
    const [agentWiseAgentCompetencyData, setAgentWiseAgentCompetencyData] = useState([])
    const [competencyMetrics, setCompetencyMetrics] = useState([])
    const [showDetails, setShowDetails] = useState(false)
    const [service_id, setService_id] = useState(null)
    const [selectedSegment, setSelectedSegment] = useState(null)
    const [competencyCols, setCompetencyCols] = useState([])
    const [competencyTableData, setCompetencyTableData] = useState([])
    const [compSortConfig, setCompSortConfig] = useState({ field: null, direction: null })
    const [role] = useState(()=> {
      const userCookie = getCookie('dashboard_login_user_creds');
      if (userCookie) {
        const userData = JSON.parse(userCookie);
        const role = userData.role
        return role
      }
      return 'admin';
    })
    
    const [filters, setFilters] = useState(initialFilterState.filters);
    const [customStart, setCustomStart] = useState(initialFilterState.customStart);
    const [customEnd, setCustomEnd] = useState(initialFilterState.customEnd);
    const [dummyFilterData, setDummyFilterData] = useState({
                employees: [],
                times: ['Today',
                  'Yesterday',
                  'This Week',
                  "This Month",
                  "Last Month",
                  "Last 7 Days",
                  "Last 30 Days",
                  "Last 3 Months",
                  "Custom Range"],
                audio_duration: ['Less than 1 Minute', '1-2 Minutes', '3-5 Minutes', 'Above 2 Minutes', 'More than 5 Minutes', 'Custom Range']
              });
    const [multiSelectFields, setMultiSelectFields] = useState([]);
    const isMultiSelectField = (key) => Array.isArray(multiSelectFields) && multiSelectFields.includes(key);
    const [expandedSections, setExpandedSections] = useState({
      agentFeedback: false,
      businessInsights: false,
      concernsAndQueries: false,
      dispositionAndSubdisposition: false,
      customModule: false
    });
    const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);

    // Update URL whenever filters change (including advanced / CRM lead created at)
    useEffect(() => {
        if (!client_id || !service_id) return;
        
        // Debounce URL updates to avoid conflicts with navigation
        const timeoutId = setTimeout(() => {
            const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);
            const queryParameterFilters = buildParameterQueryFilters(moduleParameterFilters);
            const advancedFiltersForUrl = { ...advancedFilters };
            delete advancedFiltersForUrl.agent_feedback_multiple;
            delete advancedFiltersForUrl.business_insights_multiple;
            delete advancedFiltersForUrl.custom_module_multiple;
            delete advancedFiltersForUrl.custom_module_parameter_name;
            delete advancedFiltersForUrl.custom_module_parameter_answer;
            updateUrlWithFilters(navigate, location, { ...filters, ...queryParameterFilters }, advancedFiltersForUrl, customStart, customEnd);
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [filters, advancedFilters, customStart, customEnd, client_id, service_id]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    let [dummyAdvancedFilterData, setDummyAdvancedFilterData] =useState({
      refusal_category: [],
      conversation_outcome: [],
      open_concerns_and_queries: ['Yes', 'No'],
      audioDuration: ['Less than 1 Minute', '1-2 Minutes', '3-5 Minutes', 'Above 2 Minutes', 'More than 5 Minutes'],
      conversation_potential: [],
      agent_feedback_parameters: [],
      concern_categories: [],
      concern_subcategories: [],
      query_categories: [],
      query_subcategories: [],
      disposition: [],
      subdisposition: [],
      lead_sources: [],
      primary_languages: [],
      secondary_languages: [],
      lead_sub_sources: [],
      tags: [],
      tags2: [],
      tags3: [],
      tags4: [],
      tags5: [],
      tags6: [],
      tags7: [],
      tags8: [],
      tags9: [],
      tags10: [],
      tags11: [],
      tags12: [],
      tags13: [],
      tags14: [],
      tags15: [],
      tags16: [],
      tags17: [],
      tags18: [],
      tags19: [],
      tags20: [],
      tags21: [],
      tags22: [],
      tags23: [],
      tags24: [],
      tags25: [],
      tags26: [],
      tags27: [],
      tags28: [],
      tags29: [],
      tags30: [],
      campaign_ids: [],
      lead_stages: [],
      lead_sub_stages: [],
      conversation_type_reason: [],
      custom_module: [],
    })
    const toggleAdvancedFilters = () => {
      setShowAdvancedFilters(!showAdvancedFilters);
    };

    const handleFilterChange = (e, key) => {
      const rawValue = e.target.value;
      const value = isMultiSelectField(key)
        ? (Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []))
        : rawValue;
      
      // If changing time filter and it's not "Custom Range", clear the custom date values
      if (key === 'time' && !Array.isArray(value) && value !== 'Custom Range') {
        setFilters((prev) => ({ 
          ...prev, 
          [key]: value,
          customRangeType: 'between',
        }));
        setCustomStart('');
        setCustomEnd('');
      } else if (key === 'audioduration' && !Array.isArray(value) && value !== 'Custom Range') {
        setFilters((prev) => ({
          ...prev,
          [key]: value,
          audioDurationLower: '',
          audioDurationUpper: '',
        }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
              };
    const getAudioDurationValueForApi = () => {
      if (filters.audioduration !== 'Custom Range') return filters.audioduration;
      if (filters.audioDurationLower === '' || filters.audioDurationUpper === '') return '';
      const lower = Number(filters.audioDurationLower);
      const upper = Number(filters.audioDurationUpper);
      if (Number.isNaN(lower) || Number.isNaN(upper) || lower < 0 || upper < 0 || lower > upper) return '';
      return `${lower}-${upper}`;
    };
        
          // State to track which metric's graph is being displayed
    const [activeMetric, setActiveMetric] = useState('calls');
    // Ensure performance metrics default selection stays aligned with first metric when data loads
    useEffect(() => {
      if (Array.isArray(performanceMetrics) && performanceMetrics.length > 0) {
        const exists = performanceMetrics.some(m => m.id === activeMetric);
        if (!exists) {
          setActiveMetric(performanceMetrics[0].id);
        }
      }
    }, [performanceMetrics]);

    const transformMetrics = (data) => {
      const lastData = data['last_current_period'];
      const prevData = data['percentage_change']
      return [
        {
          id: 'calls',
          title: '#Calls',
          value: lastData.calls,
          change: prevData.calls,
          color: '#4F46E5',
          allowVisit: true,
          formatter: (value) => value
        },
        {
          id: 'chats',
          title: '#Chats',
          value: lastData.chats,
          change: prevData.chats,
          color: '#4F46E5',
          allowVisit: true,
          formatter: (value) => value
        },
        {
          id: 'connected_calls',
          title: '#Connected Calls',
          value: lastData.connected_calls,
          change: prevData.connected_calls,
          color: '#4F46E5',
          allowVisit: false,
          formatter: (value) => value
        },
        {
          id: 'agentScore',
          title: 'Agent Score',
          value: lastData.agent_score !== null && lastData.agent_score !== '' ? Math.round(lastData.agent_score, 2) : '',
          change: prevData.agent_score,
          color: '#4F46E5',
          allowVisit: false,
          formatter: (value) => value
        },
        {
          id: 'needsReview',
          title: '#Needs Review',
          value: lastData.needs_review,
          change: prevData.needs_review,
          color: '#4F46E5',
          allowVisit: true,
          formatter: (value) => value
        },
        {
          id: 'fatalCalls',
          title: '#Fatal Calls',
          value: lastData.fatal_calls,
          change: prevData.fatal_calls,
          color: '#4F46E5',
          allowVisit: true,
          formatter: (value) => value
        },
        {
          id: 'aht',
          title: 'AHT',
          value: Math.round(lastData.aht, 0),
          change: prevData.aht,
          color: '#4F46E5',
          allowVisit: false,
          formatter: (value) => formatTime(value)
        },
        {
          id: 'total_talk_time',
          title: 'Total Talk Time',
          value: Math.round(lastData.total_talk_time, 0),
          change: prevData.total_talk_time,
          color: '#4F46E5',
          allowVisit: false,
          formatter: (value) => formatTime(value)
        },
        {
          id: 'frt',
          title: 'FRT',
          value: Math.round(lastData.frt, 0),
          change: prevData.frt,
          color: '#4F46E5',
          allowVisit: false,
          formatter: (value) => formatTime(value)
        },
        {
          id: 'rt',
          title: 'RT',
          value: Math.round(lastData.rt, 0),
          change: prevData.rt,
          color: '#4F46E5',
          allowVisit: false,
          formatter: (value) => formatTime(value)
        }
      ];
    };
    const handleSegmentClick = (data) => {
      const filter_applied = buildCallsPageDrilldownFilterApplied(filters, advancedFilters, {
        customStart,
        customEnd,
      });
      Object.assign(
        filter_applied,
        getConversationSegmentAdvancedFilterPatch({ id: data?.id, name: data?.name, filter_applied })
      );
      setSelectedSegment({
        ...data,
        type: 'agent_feedback_parameters',
        filter_applied,
      });
      setShowDetails(true);
    };
  const getEmployeeFromRow = (rowObj) => {
    if (!rowObj || typeof rowObj !== 'object') return null;
    if (rowObj.name) return rowObj.name;
    const keys = Object.keys(rowObj);
    const nameKey = keys.find(k => String(k).toLowerCase().includes('name'))
                 || keys.find(k => String(k).toLowerCase().includes('employee'))
                 || null;
    return nameKey ? rowObj[nameKey] : null;
  }
  const openRecordsForMetric = (metricKey, rowObj) => {
    const employeeName = getEmployeeFromRow(rowObj);
    const filter_applied = buildCallsPageDrilldownFilterApplied(filters, advancedFilters, {
      customStart,
      customEnd,
    });
    if (employeeName) filter_applied.employee = employeeName;
    const segmentId = metricKey === 'fatal' ? 'fatalCalls' : metricKey === 'needs_review' ? 'needsReview' : null;
    Object.assign(
      filter_applied,
      getConversationSegmentAdvancedFilterPatch({ id: segmentId, filter_applied })
    );
    setSelectedSegment({
      id: segmentId,
      name: segmentId ? 'Yes' : null,
      type: 'agent-wise',
      filter_applied,
    });
    setShowDetails(true);
  };
        
          // Format time (seconds to mm:ss)
          const formatTime = (seconds) => {
            const hrs = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);
          
            if (hrs > 0) {
              return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            } else {
              return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }
          };
          
        
          // Metric cards configuration
 
  const getFilterDetails = async () => {
    try {
      let res = await server.getAdvancedFilterDetailsAgentFeedback(client_id, service_id, email, 'agent-feedback', 'overview')
      if(res.status === 200){
        let resp = await res.json()
        setMultiSelectFields(Array.isArray(resp.multiselects_list) ? resp.multiselects_list : []);
      setDummyAdvancedFilterData((prev) => ({
        ...prev, // Keep the existing state
        employees: resp.employee_id,
        teams: resp.team_names || resp.team_name || resp.teams || [],
        conversation_type_reason: resp.conversation_type_reason,
        conversation_outcome: resp.conversation_outcome_list,
        refusal_category: resp.refusal_category_list,
        conversation_potential: resp.conversation_potential,
        agent_feedback_parameters: resp.agent_feedback_parameters,
        concern_categories: resp.concerns_category,
        concern_subcategories: resp.concerns_subcategory,
        query_categories: resp.query_category,
        query_subcategories: resp.query_subcategory,
        business_insights_parameters: resp.business_insights,
        disposition: resp.disposition,
        subdisposition: resp.subdisposition,
        lead_sources: resp.lead_sources,
        primary_languages: resp.primary_languages || resp.primary_language || [],
        secondary_languages: resp.secondary_languages || [],
        lead_sub_sources: resp.lead_sub_sources,
        tags: resp.tags,
        tags2: resp.tags2 || resp.tag2 || resp.tags,
        tags3: resp.tags3 || resp.tag3 || resp.tags,
        tags4: resp.tags4 || resp.tag4 || resp.tags,
        tags5: resp.tags5 || resp.tag5 || resp.tags,
        tags6: resp.tags6 || resp.tag6 || resp.tags,
        tags7: resp.tags7 || resp.tag7 || resp.tags,
        tags8: resp.tags8 || resp.tag8 || resp.tags,
        tags9: resp.tags9 || resp.tag9 || resp.tags,
        tags10: resp.tags10 || resp.tag10 || resp.tags,
        tags11: resp.tags11 || resp.tag11 || resp.tags,
        tags12: resp.tags12 || resp.tag12 || resp.tags,
        tags13: resp.tags13 || resp.tag13 || resp.tags,
        tags14: resp.tags14 || resp.tag14 || resp.tags,
        tags15: resp.tags15 || resp.tag15 || resp.tags,
        tags16: resp.tags16 || resp.tag16 || resp.tags,
        tags17: resp.tags17 || resp.tag17 || resp.tags,
        tags18: resp.tags18 || resp.tag18 || resp.tags,
        tags19: resp.tags19 || resp.tag19 || resp.tags,
        tags20: resp.tags20 || resp.tag20 || resp.tags,
        tags21: resp.tags21 || resp.tag21 || resp.tags,
        tags22: resp.tags22 || resp.tag22 || resp.tags,
        tags23: resp.tags23 || resp.tag23 || resp.tags,
        tags24: resp.tags24 || resp.tag24 || resp.tags,
        tags25: resp.tags25 || resp.tag25 || resp.tags,
        tags26: resp.tags26 || resp.tag26 || resp.tags,
        tags27: resp.tags27 || resp.tag27 || resp.tags,
        tags28: resp.tags28 || resp.tag28 || resp.tags,
        tags29: resp.tags29 || resp.tag29 || resp.tags,
        tags30: resp.tags30 || resp.tag30 || resp.tags,
        campaign_ids: resp.campaign_ids,
        lead_stages: resp.lead_stages,
        lead_sub_stages: resp.lead_sub_stages,
        custom_module: resp.custom_module
    }));
      }
    } catch (error) {
    }
  }
  // Function to get disposition keys (for the disposition dropdown)
  const getDispositionKeys = () => {
    if (!dummyAdvancedFilterData.subdisposition || typeof dummyAdvancedFilterData.subdisposition !== 'object') {
      return dummyAdvancedFilterData.disposition || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.subdisposition);
    return keys;
  };

  const getSubdispositionsForDisposition = (selectedDisposition) => {
    if (!dummyAdvancedFilterData.subdisposition) {
      return [];
    }
    
    // If no disposition selected, return all subdispositions
    if (!selectedDisposition) {
      if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
        // Flatten all subdispositions from all dispositions
        return Object.values(dummyAdvancedFilterData.subdisposition).flat();
      }
      return dummyAdvancedFilterData.subdisposition || [];
    }
    
    if (typeof dummyAdvancedFilterData.subdisposition === 'object' && !Array.isArray(dummyAdvancedFilterData.subdisposition)) {
      const subdispositions = dummyAdvancedFilterData.subdisposition[selectedDisposition] || [];
      return subdispositions;
    }
    
    return dummyAdvancedFilterData.subdisposition || [];
  };

  // Enhanced handler for disposition changes
  const handleDispositionChange = (e) => {
    const newDisposition = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      disposition: newDisposition
    }));
    
    if (newDisposition) {
      const validSubdispositions = getSubdispositionsForDisposition(newDisposition);
      const currentSubdisposition = advancedFilters.subdisposition;
      
      // If current subdisposition is not valid for new disposition, clear it
      if (currentSubdisposition && !validSubdispositions.includes(currentSubdisposition)) {
        setAdvancedFilters(prev => ({
          ...prev,
          subdisposition: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        subdisposition: ''
      }));
    }
  };
  // Function to get concern category keys (for the concern category dropdown)
  const getConcernCategoryKeys = () => {
    if (!dummyAdvancedFilterData.concern_subcategories || typeof dummyAdvancedFilterData.concern_subcategories !== 'object') {
      return dummyAdvancedFilterData.concern_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.concern_subcategories);
    return keys;
  };

  const getConcernSubcategoriesForCategory = (selectedConcernCategory) => {
    if (!dummyAdvancedFilterData.concern_subcategories) {
      return [];
    }
    
    // If no concern category selected, return all subcategories
    if (!selectedConcernCategory) {
      if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.concern_subcategories).flat();
      }
      return dummyAdvancedFilterData.concern_subcategories || [];
    }
    
    if (typeof dummyAdvancedFilterData.concern_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.concern_subcategories)) {
      const subcategories = dummyAdvancedFilterData.concern_subcategories[selectedConcernCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.concern_subcategories || [];
  };

  // Enhanced handler for concern category changes
  const handleConcernCategoryChange = (e) => {
    const newConcernCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      concernCategory: newConcernCategory
    }));
    
    if (newConcernCategory) {
      const validSubcategories = getConcernSubcategoriesForCategory(newConcernCategory);
      const currentSubcategory = advancedFilters.concernSubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          concernSubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        concernSubcategory: ''
      }));
    }
  };

  // Function to get query category keys (for the query category dropdown)
  const getQueryCategoryKeys = () => {
    if (!dummyAdvancedFilterData.query_subcategories || typeof dummyAdvancedFilterData.query_subcategories !== 'object') {
      return dummyAdvancedFilterData.query_categories || [];
    }
    const keys = Object.keys(dummyAdvancedFilterData.query_subcategories);
    return keys;
  };

  const getQuerySubcategoriesForCategory = (selectedQueryCategory) => {
    if (!dummyAdvancedFilterData.query_subcategories) {
      return [];
    }
    
    // If no query category selected, return all subcategories
    if (!selectedQueryCategory) {
      if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
        // Flatten all subcategories from all categories
        return Object.values(dummyAdvancedFilterData.query_subcategories).flat();
      }
      return dummyAdvancedFilterData.query_subcategories || [];
    }
    
    if (typeof dummyAdvancedFilterData.query_subcategories === 'object' && !Array.isArray(dummyAdvancedFilterData.query_subcategories)) {
      const subcategories = dummyAdvancedFilterData.query_subcategories[selectedQueryCategory] || [];
      return subcategories;
    }
    
    return dummyAdvancedFilterData.query_subcategories || [];
  };

  // Enhanced handler for query category changes
  const handleQueryCategoryChange = (e) => {
    const newQueryCategory = e.target.value;
    
    setAdvancedFilters(prev => ({
      ...prev,
      queryCategory: newQueryCategory
    }));
    
    if (newQueryCategory) {
      const validSubcategories = getQuerySubcategoriesForCategory(newQueryCategory);
      const currentSubcategory = advancedFilters.querySubcategory;
      
      // If current subcategory is not valid for new category, clear it
      if (currentSubcategory && !validSubcategories.includes(currentSubcategory)) {
        setAdvancedFilters(prev => ({
          ...prev,
          querySubcategory: ''
        }));
      }
    } else {
      setAdvancedFilters(prev => ({
        ...prev,
        querySubcategory: ''
      }));
    }
  };
  // Custom Module helpers
  const getCustomModuleKeys = () => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module)) return [];
    return dummyAdvancedFilterData.custom_module.map(([key]) => key);
  };
  const getCustomModuleValues = (selectedParameter) => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module) || !selectedParameter) return [];
    const match = dummyAdvancedFilterData.custom_module.find(([key]) => key === selectedParameter);
    return match ? (Array.isArray(match[1]) ? match[1] : []) : [];
  };
  const handleCustomModuleParameterChange = (e) => {
    const param = e.target.value;
    setAdvancedFilters(prev => ({
      ...prev,
      custom_module_parameter_name: param,
      custom_module_parameter_answer: getCustomModuleValues(param).includes(prev.custom_module_parameter_answer) ? prev.custom_module_parameter_answer : ''
    }));
  };
  const handleNumberInputChange = (e, type) => {
    const value = e.target.value;
    
    // if (isNaN(value)) return;
    
    if (type === 'scoreLower') {
      setAdvancedFilters(prev => ({ ...prev, scoreLower: value}));
    } else if (type === 'scoreUpper') {
      setAdvancedFilters(prev => ({ ...prev, scoreUpper: value }));
    } else if(type === 'agentFeedbackParameterScoreLower'){
      setAdvancedFilters(prev => ({...prev, agentFeedbackParameterScoreLower: value}))
    }else if(type === 'agentFeedbackParameterScoreUpper'){
      setAdvancedFilters(prev => ({...prev, agentFeedbackParameterScoreUpper: value}))
    }
  };
  const updateAgentFeedback = (index, field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      agent_feedback_multiple: prev.agent_feedback_multiple.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const updateBusinessInsights = (index, field, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      business_insights_multiple: prev.business_insights_multiple.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };
  const getBusinessInsightValues = (selectedParameter) => {
    if (!selectedParameter || !dummyAdvancedFilterData.business_insights_parameters) return [];
    
    // If business_insights_parameters is an object with key-value pairs
    if (typeof dummyAdvancedFilterData.business_insights_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      // Convert display name back to key format
      const keyFormat = selectedParameter.toLowerCase().replace(/ /g, '_');
      return dummyAdvancedFilterData.business_insights_parameters[keyFormat] || [];
    }
    
    // If business_insights_parameters is an array of arrays like [['key', ['values']], ...]
    if (Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      const selectedInsight = dummyAdvancedFilterData.business_insights_parameters.find(
        ([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) === selectedParameter
      );
      return selectedInsight ? selectedInsight[1] : [];
    }
    
    return [];
  };

  // Get business insight parameter keys (formatted for display)
  const getBusinessInsightKeys = () => {
    if (!dummyAdvancedFilterData.business_insights_parameters) return [];
    
    // If it's an object, get the keys
    if (typeof dummyAdvancedFilterData.business_insights_parameters === 'object' && !Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      return Object.keys(dummyAdvancedFilterData.business_insights_parameters).map(key => 
        key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      );
    }
    
    // If it's an array of arrays, get the first element of each sub-array
    if (Array.isArray(dummyAdvancedFilterData.business_insights_parameters)) {
      return dummyAdvancedFilterData.business_insights_parameters.map(([key]) => 
        key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      );
    }
    
    return [];
  };
  const handleAdvancedInputChange = (e, key) => {
    const value = e.target.value;
    setAdvancedFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const metricsAbortRef = useRef(null);
  const getMetrics = () => {
    try {
      if(metricsAbortRef.current){
        metricsAbortRef.current.abort();
      }
      const controller = new AbortController();
      metricsAbortRef.current = controller;
      if (
        filters.time === 'Custom Range' &&
        (customStart === '' || (filters.customRangeType !== "after" && customEnd === ''))
      ) {
        return;
      }

      // reset loading flags
      setLoadingMetrics(true);
      setLoadingCompetency(true);
      const moduleParameterFilters = buildModuleParameterFilters(filters, advancedFilters);

      /* A) Metrics & Trend */
      let basePerfData = null; // to reuse for competency table later
      Promise.allSettled([
        server.getPerformanceMetrics(client_id, service_id, email, 'agent-feedback', 'agent-wise', filters.employee, filters.time, getAudioDurationValueForApi(), customStart, customEnd, filters.record_type, filters.team_name, advancedFilters.customerId, advancedFilters.fatal, advancedFilters.openConcerns, advancedFilters.type, advancedFilters.conversationPotential, advancedFilters.scoreLower, advancedFilters.scoreUpper, advancedFilters.agent_feedback_multiple, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.conversation_direction, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.campaignId, advancedFilters.actorType, advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.conversation_type_reason, advancedFilters.first_proper_conversation, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters)),
        server.getAgentWisePerformanceDistribution(client_id, service_id, email, 'agent-feedback', 'agent-wise', filters.employee, filters.time, getAudioDurationValueForApi(), customStart, customEnd, filters.record_type, ['calls', 'chats', 'connected_calls', 'agentScore', 'needsReview', 'fatalCalls', 'aht','total_talk_time', 'frt', 'rt', 'inbound_missed_calls', 'outbound_not_connected_calls', 'inbound_calls', 'outbound_calls','agent_talk_duration', 'customer_talk_duration', 'calls_where_customer_talk_duration_lt_20'], advancedFilters.customerId, advancedFilters.fatal, advancedFilters.openConcerns, advancedFilters.type, advancedFilters.conversationPotential, advancedFilters.scoreLower, advancedFilters.scoreUpper, advancedFilters.agent_feedback_multiple, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.conversation_direction, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.campaignId, advancedFilters.actorType, advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.first_proper_conversation, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name)
      ]).then(async ([res1, res6]) => {
        const r1 = res1.status === 'fulfilled' ? res1.value : null;
        const r6 = res6.status === 'fulfilled' ? res6.value : null;
        if (r1 && r1.status === 200) {
          const j1 = await r1.json();
          setPerformaceMetrics(transformMetrics(j1.data));
        }
        if (r6 && r6.status === 200) {
          const j6 = await r6.json();
          setAgentWiseDailyTrendData(j6.data);
          basePerfData = j6.data; // save raw data
        }
      }).finally(()=> setLoadingMetrics(false));

      /* D) Competency scores & trend */
      server.getDetailedCompetencyScores(client_id, service_id, email, 'agent-feedback', 'agent-wise', filters.employee, filters.time, getAudioDurationValueForApi(), customStart, customEnd, filters.record_type, advancedFilters.customerId, advancedFilters.fatal, advancedFilters.openConcerns, advancedFilters.type, advancedFilters.conversationPotential, advancedFilters.scoreLower, advancedFilters.scoreUpper, advancedFilters.agent_feedback_multiple, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.conversation_direction, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.campaignId, advancedFilters.actorType, advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.conversation_type_reason, advancedFilters.first_proper_conversation, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name)
        .then(async r7 => {
          if (r7 && r7.status === 200) {
            const j7 = await r7.json();
            const defaultColor = "#4F46E5";
            const callMetrics = j7.data.map(({ name, value, percentage }) => ({
              id: name,
              title: name,
              value: value,
              change: percentage,
              color: defaultColor,
              formatter: (val) => val
            }));
            setCompetencyMetrics(callMetrics);
            // setCompetencyScoresData(j7.data ?? []); // This line was removed from the new_code, so it's removed here.

            // trend per competency
            const paramsTemp = j7.data.map(d => d.name);
            return server.getAgentWisePerformanceDistribution(client_id, service_id, email, 'agent-feedback', 'agent-wise', filters.employee, filters.time, getAudioDurationValueForApi(), customStart, customEnd, filters.record_type, paramsTemp, advancedFilters.customerId, advancedFilters.fatal, advancedFilters.openConcerns, advancedFilters.type, advancedFilters.conversationPotential, advancedFilters.scoreLower, advancedFilters.scoreUpper, advancedFilters.agent_feedback_multiple, advancedFilters.business_insights_multiple, advancedFilters.concernCategory, advancedFilters.concernSubcategory, advancedFilters.queryCategory, advancedFilters.querySubcategory, advancedFilters.disposition, advancedFilters.subdisposition, advancedFilters.conversation_direction, advancedFilters.lead_source, advancedFilters.primary_language, advancedFilters.secondary_languages, advancedFilters.lead_sub_source, advancedFilters.tag, advancedFilters.tag2, advancedFilters.tag3, ...getAdditionalTagFilterValues(advancedFilters), advancedFilters.campaignId, advancedFilters.actorType, advancedFilters.crm_lead_stage, advancedFilters.crm_lead_sub_stage, advancedFilters.customer_phone_number, advancedFilters.crm_location, advancedFilters.first_proper_conversation, advancedFilters.custom_module_parameter_name, advancedFilters.custom_module_parameter_answer, controller.signal, moduleParameterFilters, ...getCrmLeadCreatedAtApiArgs(advancedFilters), filters.team_name);
          }
          return null;
        }).catch(err=>{ if(err.name!=='AbortError'){} return null; })
        .then(async r8 => {
          if (r8 && r8.status === 200) {
            const j8 = await r8.json();
            setAgentWiseAgentCompetencyData(j8.data);

            // Build competency table combining base metrics (calls/chats etc.) with competency scores
            if (basePerfData && basePerfData.length > 0 && j8.data && j8.data.length > 0) {
              const cols = [...new Set([...Object.keys(basePerfData[0]), ...Object.keys(j8.data[0])])];
              setCompetencyCols(cols);
              let arr = [...basePerfData]
              for (let i=0; i<j8.data.length; i++){
                if(arr.find(item => item.name === j8.data[i].name)){
                  arr[arr.findIndex(item => item.name === j8.data[i].name)] = {...arr[arr.findIndex(item => item.name === j8.data[i].name)], ...j8.data[i]}
                }else{
                  arr.push({...j8.data[i]})
                }
              }
              setCompetencyTableData(arr);
            }
          }
        }).finally(()=> setLoadingCompetency(false));
    } catch(err){ if(err.name!=='AbortError'){} }
  }
  const handleDownloadCSV = () => {
    // Determine which dataset to use
    const data = competencyTableData;
    
    if (!data || data.length === 0) {
      return;
    }
    
    // Get all possible headers from the objects
    const headers = Array.from(
      new Set(
        data.flatMap(item => Object.keys(item))
      )
    );
    
    downloadRowsAsWorkbook(
      data.map((item) => headers.reduce((row, header) => ({...row, [header]: item[header] === undefined ? '' : item[header]}), {})),
      `Agent_Competency_Assessment_${new Date().toISOString().slice(0, 10)}`
    );
  };
  const handleChartDownload = (data, title, options = {}) => {
    if (options.workbook) {
      downloadWorkbook(data, `${title}_data.xlsx`);
      return;
    }
    downloadRowsAsWorkbook(data, title);
  };

  // Sorting helpers for competency table
  const getValueForSortComp = (item, field) => {
    if (!item) return null;
    const v = item[field];
    if (v == null) return null;
    const num = Number(v);
    if (!Number.isNaN(num) && String(v).trim() !== '') return num;
    return String(v).toLowerCase();
  };

  const sortArrayComp = (list, field, direction) => {
    if (!field || !direction) return list;
    const arr = [...list];
    const factor = direction === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = getValueForSortComp(a, field);
      const vb = getValueForSortComp(b, field);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return factor * (va - vb);
      return factor * String(va).localeCompare(String(vb));
    });
    return arr;
  };

  const arrowClass = (cfg, field, dir) => (cfg.field === field && cfg.direction === dir ? 'text-indigo-600' : 'text-gray-300 group-hover:text-gray-400');
  const getAriaSort = (cfg, field) => (cfg.field === field ? (cfg.direction === 'asc' ? 'ascending' : 'descending') : 'none');
  const cycleSort = (cfg, setCfg, field) => {
    let nextDir = 'asc';
    if (cfg.field === field) {
      nextDir = cfg.direction === 'asc' ? 'desc' : (cfg.direction === 'desc' ? null : 'asc');
    }
    setCfg(nextDir ? { field, direction: nextDir } : { field: null, direction: null });
  };
  const renderArrows = (cfg, field, setCfg) => {
    if (cfg.field === field && cfg.direction === 'asc') {
      return (
        <ArrowUp className={`h-3 w-3 ${arrowClass(cfg, field, 'asc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
      );
    }
    if (cfg.field === field && cfg.direction === 'desc') {
      return (
        <ArrowDown className={`h-3 w-3 -mt-0.5 ${arrowClass(cfg, field, 'desc')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'desc' });}} />
      );
    }
    return (
      <ChevronsUpDown className={`h-3 w-3 ${arrowClass(cfg, field, 'none')}`} onClick={(e)=>{e.stopPropagation(); setCfg({ field, direction: 'asc' });}} />
    );
  };

  const displayCompetencyTableData = useMemo(() => (
    compSortConfig.field ? sortArrayComp(competencyTableData, compSortConfig.field, compSortConfig.direction) : competencyTableData
  ), [competencyTableData, compSortConfig]);
  const moduleParameterQueryFilters = buildParameterQueryFilters(buildModuleParameterFilters(filters, advancedFilters));
  useEffect(() => {
              const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
              if (user) {
                setEmail(user.email);
              }
              if (serviceID) {
                setService_id(serviceID);
              }
              if(clientID){
                setClient_id(clientID)
              }
              if(role === 'employee'){
                handleFilterChange({'target': {'value':user.name}}, 'employee')
              }
            }, [serviceID, clientID, role]);
          
  // Initial load - call immediately when client_id, service_id, email are first set
  useEffect(() => {
              if (client_id && service_id && email) {
                getMetrics();
                getFilterDetails();
              }
            }, [client_id, service_id, email, customStart, customEnd]);
            useEffect(() => {
              if (!client_id || !service_id) return;
          
              // Skip debounce logic on initial mount to avoid duplicate first fetch
              if (firstFiltersLoadRef.current) {
                firstFiltersLoadRef.current = false;
                return;
              }
          
              // Clear previous debounce timer
              if (filtersDebounceRef.current) {
                clearTimeout(filtersDebounceRef.current);
              }
          
              filtersDebounceRef.current = setTimeout(() => {
                // Only reset to first page; page effect will handle fetching once
                getMetrics();
              }, 600); // idle delay
          
              return () => {
                if (filtersDebounceRef.current) {
                  clearTimeout(filtersDebounceRef.current);
                }
              };
            }, [filters, advancedFilters, client_id, service_id, customStart, customEnd]);
    useEffect(() => {
          const handleEsc = (event) => {
            if (event.key === "Escape") {
              setShowDetails(false);
              setShowAdvancedFilters(false);
            }
          }
          if (showDetails || showAdvancedFilters) {
            window.addEventListener('keydown', handleEsc);
          }
          return () => {
            window.removeEventListener('keydown', handleEsc);
          }
        }, [showDetails, showAdvancedFilters])

    return (
            <div className="py-3 px-5 bg-gray-50 font-sans">
                <h1 class="text-xl font-semibold mb-2">
                  <span class="text-gray-500">Agent Feedback &gt;</span>
                  <span class="text-gray-900">  Agent Comparison</span>
                </h1>
                <div className="text-sm text-gray-500">Compare perfomance between Agents</div>
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4 mt-4">
                <div className="flex justify-between mb-4 items-center">
                <div className="text-sm font-medium text-gray-700">Quick Filters</div>
                <div><button 
                  className="flex items-center text-indigo-600 text-sm"
                  onClick={toggleAdvancedFilters}
                >
                  <FaFilter className="mr-2" />
                  Show Advanced Filters
                </button></div>
                </div>
                                <div className="flex gap-4 mb-4">
                                        <ModernTimeDropdown placeholder="Select Team" value={filters.team_name} onChange={(e) => handleFilterChange(e, 'team_name')} options={dummyAdvancedFilterData.teams} isMulti={isMultiSelectField('team_name')}/>
                                        <ModernTimeDropdown isDisabled={role === 'employee'} placeholder="Select Employee" value={filters.employee} onChange={(e) => handleFilterChange(e, 'employee')} options={dummyAdvancedFilterData.employees} isMulti={isMultiSelectField('employee')}/>
                                        <ModernTimeDropdown placeholder="Select Time Duration" value={filters.time} onChange={(e) => handleFilterChange(e, 'time')} options={dummyFilterData.times} isMulti={isMultiSelectField('time')}/>
                                        <ModernTimeDropdown placeholder="Select Audio Duration" value={filters.audioduration} onChange={(e) => handleFilterChange(e, 'audioduration')} options={dummyFilterData.audio_duration} isMulti={isMultiSelectField('audioduration')}/>
                                        <ModernTimeDropdown placeholder="Select Record Type" value={filters.record_type} onChange={(e) => handleFilterChange(e, 'record_type')} options={['call', 'chat']} isMulti={isMultiSelectField('record_type')}/>
                                        </div>
                                        {filters.time === "Custom Range" && (
                                          <div className="mb-2">
                                            <ModernDateRangePicker
                                              startDate={customStart}
                                              endDate={customEnd}
                                              rangeType={filters.customRangeType || "between"}
                                              maxRangeDays={90}
                                              placeholder="Select Time Range"
                                              onChange={({ startDate, endDate, rangeType }) => {
                                                setCustomStart(startDate);
                                                setCustomEnd(endDate);
                                                setFilters((prev) => ({
                                                  ...prev,
                                                  customRangeType: rangeType || "between",
                                                }));
                                              }}
                                            />
                                          </div>
                                        )}
                                        {filters.audioduration === "Custom Range" && (
                                          <div className="flex gap-4 mb-2">
                                            <input
                                              type="number"
                                              min="0"
                                              value={filters.audioDurationLower ?? ''}
                                              onChange={(e) => handleFilterChange(e, 'audioDurationLower')}
                                              placeholder="Audio Duration Lower Limit (sec)"
                                              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
                                            />
                                            <input
                                              type="number"
                                              min="0"
                                              value={filters.audioDurationUpper ?? ''}
                                              onChange={(e) => handleFilterChange(e, 'audioDurationUpper')}
                                              placeholder="Audio Duration Upper Limit (sec)"
                                              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
                                            />
                                          </div>
                                        )}
                                {/* Filter Display */}
                <div className="flex gap-4 mb-4 mt-4 flex-wrap">
                  {Object.keys(filters).map(
                    (key) => {
                      // Skip customStart and customEnd if time is not "Custom Range"
                      if (key === 'customStart' || key === 'customEnd' || key === 'customRangeType') {
                        return null;
                      }
                      if (key === 'time' && filters.time === 'Custom Range') {
                        return null;
                      }
                      if (key === 'audioDurationLower' || key === 'audioDurationUpper') {
                        return null;
                      }
                      if (key === 'audioduration' && filters.audioduration === 'Custom Range') {
                        return null;
                      }
                      
                      return (Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key]) && (
                        <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                          <span className="capitalize text-indigo-600">{key} - {Array.isArray(filters[key]) ? (filters[key].length > 2 ? `${filters[key].slice(0, 2).join(', ')} +${filters[key].length - 2} more` : filters[key].join(', ')) : filters[key]}</span>
                          {((key!=='time') && !(role === 'employee' && key === 'employee')) && <button
                            className="ml-2 text-gray-600"
                            onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                          >
                            <FaTimes />
                          </button>}
                        </div>
                      );
                    }
                  )}
                  {filters.time === 'Custom Range' && customStart && (filters.customRangeType === "after" || customEnd) && (
                    <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                      <span className='text-indigo-600'>
                        Time - {customStart} {filters.customRangeType === "after" ? "onwards" : `to ${customEnd}`}
                      </span>
                      <button
                        className="ml-2 text-gray-600"
                        onClick={() => {
                          setFilters((prev) => ({ ...prev, time: '', customRangeType: 'between' }));
                          setCustomStart('');
                          setCustomEnd('');
                        }}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                  {filters.audioduration === 'Custom Range' && filters.audioDurationLower !== '' && filters.audioDurationUpper !== '' && (
                    <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                      <span className='text-indigo-600'>Audio Duration - {filters.audioDurationLower} to {filters.audioDurationUpper}</span>
                      <button
                        className="ml-2 text-gray-600"
                        onClick={() => setFilters((prev) => ({ ...prev, audioduration: '', audioDurationLower: '', audioDurationUpper: '' }))}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                  {/* Show advanced filters that are set (excluding defaults) */}
                  {Object.entries(moduleParameterQueryFilters).map(([key, val]) => {
                    if (filters[key] !== undefined) return null;
                    if (key.endsWith('_lower') || key.endsWith('_upper')) return null;
                    return (
                      <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                        <span className='capitalize text-indigo-600'>{key} - {String(val)}</span>
                        <button
                          className="ml-2 text-gray-600"
                          onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    );
                  })}
                  {Array.from(
                    new Set(
                      Object.keys(moduleParameterQueryFilters)
                        .filter((key) => key.endsWith('_lower') || key.endsWith('_upper'))
                        .map((key) => key.replace(/_(lower|upper)$/, ''))
                    )
                  ).map((baseKey) => {
                    const lower = moduleParameterQueryFilters[`${baseKey}_lower`];
                    const upper = moduleParameterQueryFilters[`${baseKey}_upper`];
                    if (lower === undefined && upper === undefined) return null;
                    return (
                      <div key={`${baseKey}-range`} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                        <span className='capitalize text-indigo-600'>{baseKey} - {String(lower ?? '')} to {String(upper ?? '')}</span>
                        <button
                          className="ml-2 text-gray-600"
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              [`${baseKey}_lower`]: '',
                              [`${baseKey}_upper`]: '',
                            }))
                          }
                        >
                          <FaTimes />
                        </button>
                      </div>
                    );
                  })}
                  {Object.entries(advancedFilters).map(([key, val]) => {
                    // skip if blank or default
                    if (
                      val === '' || val== null
                    ) {
                      return null;
                    }
                    if (key === 'scoreLower' || key === 'scoreUpper') {
                      return null;
                    }
                    if (key === 'agent_feedback_multiple' || key === 'business_insights_multiple' || key === 'custom_module_multiple' || key === 'custom_module_parameter_name' || key === 'custom_module_parameter_answer' || shouldHideCrmLeadCreatedAtChipKey(key)) {
                      return null;
                    }
                    if(key !== 'agent_feedback_multiple' && key !== 'business_insights_multiple'){
                    return (
                      <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                        <span className='capitalize text-indigo-600'>{key} - {String(!isNaN(val/1) ? Number(val).toFixed(0) : val)}</span>
                        <button
                          className="ml-2 text-gray-600"
                          onClick={() => setAdvancedFilters((prev) => key === 'crm_lead_created_at' ? ({ ...prev, crm_lead_created_at: '', crm_lead_created_at_start: '', crm_lead_created_at_end: '' }) : ({ ...prev, [key]: null }))}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    );
                    }
                  })}
                  {advancedFilters.scoreLower !== null && advancedFilters.scoreLower !== '' && advancedFilters.scoreUpper !== null && advancedFilters.scoreUpper !== '' && (
                    <div className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                      <span className='text-indigo-600'>Overall Score - {advancedFilters.scoreLower} to {advancedFilters.scoreUpper}</span>
                      <button
                        className="ml-2 text-gray-600"
                        onClick={() => setAdvancedFilters((prev) => ({ ...prev, scoreLower: null, scoreUpper: null }))}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                  <CrmLeadCreatedAtFilterChips
                    advancedFilters={advancedFilters}
                    setAdvancedFilters={setAdvancedFilters}
                  />
                </div>
                            </div>
                {/* Advanced Filters Sidebar */}
                <CallAdvancedFiltersPanel
                  isOpen={showAdvancedFilters}
                  onClose={toggleAdvancedFilters}
                  multiSelectFields={multiSelectFields}
                  advancedFilters={advancedFilters}
                  setAdvancedFilters={setAdvancedFilters}
                  handleAdvancedInputChange={handleAdvancedInputChange}
                  handleNumberInputChange={handleNumberInputChange}
                  dummyAdvancedFilterData={dummyAdvancedFilterData}
                  expandedSections={expandedSections}
                  setExpandedSections={setExpandedSections}
                  updateAgentFeedback={updateAgentFeedback}
                  updateBusinessInsights={updateBusinessInsights}
                  getBusinessInsightKeys={getBusinessInsightKeys}
                  getBusinessInsightValues={getBusinessInsightValues}
                  handleConcernCategoryChange={handleConcernCategoryChange}
                  getConcernCategoryKeys={getConcernCategoryKeys}
                  getConcernSubcategoriesForCategory={getConcernSubcategoriesForCategory}
                  handleQueryCategoryChange={handleQueryCategoryChange}
                  getQueryCategoryKeys={getQueryCategoryKeys}
                  getQuerySubcategoriesForCategory={getQuerySubcategoriesForCategory}
                  handleDispositionChange={handleDispositionChange}
                  getDispositionKeys={getDispositionKeys}
                  getSubdispositionsForDisposition={getSubdispositionsForDisposition}
                  handleCustomModuleParameterChange={handleCustomModuleParameterChange}
                  getCustomModuleKeys={getCustomModuleKeys}
                  getCustomModuleValues={getCustomModuleValues}
                  showCampaignBatchId={false}
                  showNeedsReview={false}
                  crmStageKey="crm_lead_stage"
                  crmSubStageKey="crm_lead_sub_stage"
                  showCrmLeadCreatedAt={true}
                  actorTypeLabel="Actor Type"
                />
                <div>
                    <div className="py-6 px-3 flex items-center">
                        <div style={{height: "65vh", width: "100%", background: "white"}}>
                        {
                          loadingMetrics && (
                            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                              <div className="loader"></div>
                              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                            </div>
                          )
                        }
                            {!loadingMetrics && <HorizontalBarMetrics data={agentWiseDailyTrendData} 
                                metrics={performanceMetrics} 
                                activeMetric={activeMetric} 
                                setActiveMetric={setActiveMetric} 
                                formatTime={formatTime} setShowDetails={handleSegmentClick}
                                title="Performance Metrics"
                                handleDownload={handleChartDownload} />}
                        </div>
                    </div>
                    <div className="py-6 px-3 flex items-center">
                    <div style={{flex: "1", height: "65vh", overflowY: "auto"}} className="bg-white rounded-lg shadow-sm">
                            {
                              loadingCompetency && (
                                <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                                  <div className="loader"></div>
                                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                                </div>
                              )
                            }
                            {!loadingCompetency && <CompetencyAssessment title="Agent Competency Assessment" competencyData={agentWiseAgentCompetencyData} competencyMetrics={competencyMetrics} handleDownload={handleChartDownload} />}
                        </div>
                    </div>
                    <div className="py-6 px-3 flex items-center">
                      <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{width: "calc(100vw - 20rem)", maxWidth: "calc(100vw - 18rem)"}}>
                        <div className="p-2 flex justify-end items-center">
                        <button onClick={handleDownloadCSV} className="flex items-center"><Download className="ml-2" /></button>
                        </div>
                        {
                          loadingCompetency && (
                            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                              <div className="loader"></div>
                              <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                            </div>
                          )
                        }
                        {!loadingCompetency && (
                          <div className="overflow-auto" style={{overflow: "auto", maxHeight: "50vh"}}>
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50" style={{ position: "sticky", top: "0" }}>
                                <tr>
                                  {
                                    competencyCols.map((col, idx) => {
                                      return (
                                        <th key={idx} scope="col" aria-sort={getAriaSort(compSortConfig, col)} className="px-5 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap" style={{fontSize: "13px", background: "white", whiteSpace: "nowrap"}}>
                                          <button type="button" className="flex items-center group whitespace-nowrap" onClick={()=>cycleSort(compSortConfig, setCompSortConfig, col)}>
                                            <span style={{whiteSpace: 'nowrap'}}>{formatCompetencyColumnLabel(col)}</span>
                                            <span className="ml-1 flex flex-col leading-none">{renderArrows(compSortConfig, col, setCompSortConfig)}</span>
                                          </button>
                                        </th>
                                      )
                                    })
                                  }
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {
                                  displayCompetencyTableData.map((conversation, index) => {
                                    return (
                                      <tr key={`${index}-${conversation.id}`} className="hover:bg-gray-50">
                                        {
                                          competencyCols.map((col, idx)=> {
                                            const colNorm = String(col || '').toLowerCase().replace(/\s+/g, '');
                                            const value = conversation[col];
                                            const numericValue = typeof value === 'number' ? value : parseInt(value, 10);
                                            const isTalkTime = colNorm.includes('totaltalktime') || colNorm.includes('total_talk_time');
                                            const isFatal = colNorm.includes('fatal');
                                            const isNeedsRev = colNorm.includes('needsreview') || colNorm.includes('needs_review');
                                            const clickable = (isFatal || isNeedsRev) && !Number.isNaN(numericValue) && numericValue > 0;
                                            const onClick = () => openRecordsForMetric(isFatal ? 'fatal' : 'needs_review', conversation);
                                            const displayVal = isTalkTime && !Number.isNaN(numericValue) ? Math.round(numericValue / 60) : (typeof value === 'string' && value.length > 100) ? value.substr(0, 100) + "..." : value;
                                            return (
                                              <td key={`${idx}-${index}`} className="px-3 py-2 whitespace-nowrap text-sm text-gray-600" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>
                                                <span onClick={clickable ? onClick : undefined} style={{ cursor: clickable ? 'pointer' : 'default', color: clickable ? '#4f46e5' : undefined, textDecoration: clickable ? 'underline' : 'none' }}>
                                                  {displayVal}
                                                </span>
                                              </td>
                                            )
                                          })
                                        }
                                      </tr>
                                    )
                                  })
                                }
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                </div>
                {
                                showDetails && (
                                    <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                                        <div style={{position: "fixed", left: "2%", top: "4%", width: "96%", height: "92%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                                        zIndex: "999999999"}}>

                                            <div style={{position: "absolute", right: "10px", top: "10px", cursor: "pointer"}} onClick={()=>setShowDetails(false)}><FaTimes style={{color: "slategray"}}/></div>
                                            <div style={{overflow: "auto", height: "100%", borderRadius: "10px"}}>
                                              <ConversationRecords serviceID={service_id} clientID={client_id} email={email} type="modal-open" segment={selectedSegment}/>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }
                            <style jsx>
          {`
          .sections-headings {
                          cursor: pointer;
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          padding: 15px;
                          border-radius: 10px;
                          transition: background 0.3s;
                          width: 100%;
                          }

                          .section-title {
                          font-size: 15px;
                          font-weight: 500;
                          display: flex;
                          align-items: center;
                          }
          `}              
        </style>
            </div>
    );
};

export default AgentFeedbackAgentWise;
